import { useCallback, useContext, useEffect } from 'react';
import { CAContext } from '../contexts/CAContext';
import { getAvailableSchedule } from '../utils/gcp-utils';
import { isMobile } from '../utils/mobile-utils';
import { CaseDataContext } from '../contexts/CaseDataContext';
import { useChatCaseDetails } from './utility-hooks/useChatCaseDetails';
import { useGCPChat } from './utility-hooks/useGCPChat';
import { useUserInfo } from './utility-hooks/useUserInfo';
import { API_ENDPOINTS } from '../config/api-config';

export const useUtilityHooks = () => {
  const {
    chatState: { locale, tenant },
    setChatState,
  } = useContext(CAContext);

  const {
    state: { pageInfo },
    setState: setChatCaseContext,
  } = useContext(CaseDataContext);

  // Initialize all utility hooks
  const { data: userData, error: userError, loading: userInfoLoading, trigger: getUserInfo } = useUserInfo();

  const { data: gcpChatData, error: gcpChatError, loading: gcpChatLoading, trigger: getGCPChat } = useGCPChat(locale);

  const { data: chatCaseData, error: chatCaseError, loading: chatCaseLoading, trigger: getChatCaseData } = useChatCaseDetails();

  // Audio playback function
  const playSoundMessageNotif = useCallback(() => {
    try {
      const audio = new Audio(API_ENDPOINTS.NOTIFICATION_SOUND);
      audio.play().catch((error) => {
        console.info('Failed to play notification audio:', error);
      });
    } catch (error) {
      console.info('Error creating audio element:', error);
    }
  }, []);

  // Get user info on mount
  useEffect(() => {
    if (!userData) getUserInfo();
  }, [getUserInfo, userData]);

  // Get GCP chat data when locale changes
  useEffect(() => {
    if (!gcpChatData) getGCPChat();
  }, [locale, getGCPChat, gcpChatData]);

  // Get chat case data
  useEffect(() => {
    if (!chatCaseData)
      getChatCaseData({
        pageName: pageInfo?.pageName || 'Studio',
        pageSection: pageInfo?.pageSection || 'Studio',
        pageStage: pageInfo?.pageStage || 'Design',
        isMobile: isMobile(),
        locale,
        tenant: tenant || 'vistaprint',
        path: window.location.pathname,
        url: window.location.href,
      });
  }, [getChatCaseData, chatCaseData]);

  // Update case info in context
  useEffect(() => {
    if (chatCaseData) {
      setChatCaseContext((prev) => ({ ...prev, caseData: chatCaseData.caseData, queueType: chatCaseData.queueInfo }));
    }
  }, [chatCaseData]);

  // Update user info in context
  useEffect(() => {
    if (userData) {
      setChatState((prevState) => ({
        ...prevState,
        userInfo: userData,
      }));
    }
  }, [userData, setChatState]);

  // Update GCP chat data in context
  useEffect(() => {
    if (gcpChatData) {
      const schedule = getAvailableSchedule(gcpChatData?.formattedBusinessHours, gcpChatData?.timezone?.name);
      setChatState((prevState) => ({
        ...prevState,
        gcpChatData,
        availableSchedule: schedule,
      }));
    }
  }, [gcpChatData, setChatState]);

  useEffect(() => {
    if (playSoundMessageNotif)
      setChatState((prev) => ({
        ...prev,
        playSoundMessageNotif: playSoundMessageNotif,
      }));
  }, [playSoundMessageNotif]);

  return {
    utilityHooksData: {
      userData,
      gcpChatData,
      chatCaseData,
    },
    utilityHooksLoading: {
      userInfoLoading,
      gcpChatLoading,
      chatCaseLoading,
    },
    utilityHooksError: {
      userError,
      gcpChatError,
      chatCaseError,
    },
    utilityHooksTriggers: {
      getUserInfo,
      getGCPChat,
      getChatCaseData,
    },
    playSoundMessageNotif,
  };
};
